/**
 * 예약 달력 계산 (2026-08-12) — 순수 함수. React 밖에서 테스트한다.
 *
 * 캘린더 화면(MonthView/TimeGridView)은 CalendarEventDto 에 타입 결합돼 있어 그대로 못 쓴다.
 * 대신 배치 계산만 여기서 하고, 렌더는 예약 전용 컴포넌트가 담당한다.
 */
import type { ReservationDto, ReservationResourceDto } from '../../api/reservation';

export interface DayCell {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  isPast: boolean;
}

/** 월 그리드(일요일 시작, 6주 고정) — 셀 42개. */
export function buildMonthCells(cursor: Date, today: Date): DayCell[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const todayKey = dayKey(today);
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({
      date: d,
      inMonth: d.getMonth() === cursor.getMonth(),
      isToday: dayKey(d) === todayKey,
      isPast: dayKey(d) < todayKey,
    });
  }
  return cells;
}

/** 로컬 달력일 키 — UTC 로 자르면 자정 근처에서 하루가 밀린다(캘린더 도메인 기존 함정). */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 그 날 0시~24시 구간과 겹치는 예약들. */
export function reservationsOfDay(all: ReadonlyArray<ReservationDto>, day: Date): ReservationDto[] {
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0).getTime();
  const to = from + 24 * 60 * 60 * 1000;
  return all.filter((r) => {
    const s = new Date(r.startAt).getTime();
    const e = new Date(r.endAt).getTime();
    return s < to && e > from;
  });
}

/** 구간 [start,end) 에서 동시에 점유된 최대 수량. 서버 peakOccupancy 와 같은 규칙(스윕 라인). */
export function peakUsed(all: ReadonlyArray<ReservationDto>, start: Date, end: Date): number {
  const events: Array<{ t: number; d: number }> = [];
  for (const r of all) {
    const s = new Date(r.startAt).getTime();
    const e = new Date(r.endAt).getTime();
    const from = Math.max(s, start.getTime());
    const to = Math.min(e, end.getTime());
    if (to <= from) continue;
    events.push({ t: from, d: r.quantity });
    events.push({ t: to, d: -r.quantity });
  }
  events.sort((a, b) => (a.t - b.t) || (a.d - b.d));
  let cur = 0;
  let peak = 0;
  for (const ev of events) {
    cur += ev.d;
    if (cur > peak) peak = cur;
  }
  return peak;
}

export interface Slot {
  start: Date;
  end: Date;
  used: number;
  remaining: number;
  /** 과거 슬롯 — 선택 불가. */
  past: boolean;
}

/** 시간 단위 자원의 하루 슬롯 목록(운영시간 × slotMinutes). */
export function buildDaySlots(
  resource: ReservationResourceDto,
  day: Date,
  reservations: ReadonlyArray<ReservationDto>,
  now: Date,
): Slot[] {
  const slots: Slot[] = [];
  for (let m = resource.openMinute; m + resource.slotMinutes <= resource.closeMinute; m += resource.slotMinutes) {
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
    start.setMinutes(m);
    const end = new Date(start.getTime() + resource.slotMinutes * 60 * 1000);
    const used = peakUsed(reservations, start, end);
    slots.push({
      start,
      end,
      used,
      remaining: Math.max(0, resource.capacity - used),
      past: end.getTime() <= now.getTime(),
    });
  }
  return slots;
}

/** 하루 전체(운영시간 기준) 잔여 — 월 그리드 셀 배지에 쓴다. */
export function dayRemaining(
  resource: ReservationResourceDto,
  day: Date,
  reservations: ReadonlyArray<ReservationDto>,
): number {
  if (resource.bookingUnit === 'day') {
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
    return Math.max(0, resource.capacity - peakUsed(reservations, start, end));
  }
  const slots = buildDaySlots(resource, day, reservations, new Date(0));
  // 시간 단위는 "슬롯 중 하나라도 남으면 예약 가능" 이므로 최대 잔여를 보여준다.
  return slots.reduce((max, s) => Math.max(max, s.remaining), 0);
}

export function fmtTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function fmtDate(d: Date): string {
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export function fmtRange(start: Date, end: Date, unit: 'time' | 'day'): string {
  if (unit === 'day') {
    // 저장은 반납 다음날 자정이 배타적 끝 — 표시는 하루 되돌려 "8.13 ~ 8.14" 로 읽히게 한다.
    const last = new Date(end.getTime() - 1);
    return dayKey(start) === dayKey(last)
      ? fmtDate(start)
      : `${fmtDate(start)} ~ ${fmtDate(last)}`;
  }
  return `${fmtDate(start)} ${fmtTime(start)}~${fmtTime(end)}`;
}
